/**
 * Scopes (architecture §12.6): the open layer first (dialog > palette > popover — while one is open, lower scopes are
 * blocked except commands marked `overLayers`, i.e. mod+k), then the `data-scope` values of the focused element's
 * ancestors, deepest first, then `global`.
 *
 * Hosts mark a region with `scopeProps('list', provider)`: the provider tells commands what the region targets
 * (the highlighted row, the selection), read only when a command is about to run.
 */
import type { CommandTarget } from '../registry/types';

export interface ScopeTarget {
  readonly target: CommandTarget | null;
  /** The selection (ids of `target.model`), empty when nothing is selected. */
  readonly selection?: readonly string[];
}

type ScopeProvider = () => ScopeTarget;

const providers = new WeakMap<Element, ScopeProvider>();

/** Props for a scope host: `<div {...scopeProps('list', () => ({ target, selection }))}>`. */
export function scopeProps(scope: string, provider?: ScopeProvider): { 'data-scope': string; ref: (el: Element | null) => void } {
  return {
    'data-scope': scope,
    ref: (el) => {
      if (el && provider) providers.set(el, provider);
    },
  };
}

export interface ActiveScopes {
  /** Deepest first, ending with 'global' unless a layer blocks it. */
  readonly scopes: readonly string[];
  /** A modal layer (dialog, palette, alertdialog) is open. */
  readonly layer: boolean;
  readonly target: CommandTarget | null;
  readonly selection: readonly string[];
}

/** Open layers: modal dialogs and sheets, the palette, and Radix popovers / menus (their popper wrapper). */
const LAYER_SELECTOR =
  '[aria-modal="true"], [role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [data-radix-popper-content-wrapper]';

/** The active scope stack for focus at `from` (default: document.activeElement). */
export function activeScopes(from?: Element | null, doc: Document | undefined = globalThis.document): ActiveScopes {
  const focus = from ?? doc?.activeElement ?? null;
  const layers = doc ? [...doc.querySelectorAll(LAYER_SELECTOR)] : [];
  // the outermost open layer that holds focus; with layers open but focus outside them, everything is blocked
  const layer = layers.find((l) => focus && l.contains(focus) && !layers.some((o) => o !== l && o.contains(l))) ?? null;
  if (layers.length > 0 && !layer) return { scopes: [], layer: true, target: null, selection: [] };
  const scopes: string[] = [];
  let target: CommandTarget | null = null;
  let selection: readonly string[] = [];
  for (let el: Element | null = focus; el; el = el.parentElement) {
    const s = el.getAttribute('data-scope');
    if (s) {
      scopes.push(s);
      const p = providers.get(el);
      if (p && !target && selection.length === 0) {
        const t = p();
        target = t.target;
        selection = t.selection ?? [];
      }
    }
    if (el === layer) break;
  }
  if (!layer) scopes.push('global');
  return { scopes, layer: layer !== null, target, selection };
}

/**
 * The scope stack of the open entity page (an issue page, else a project page) when focus sits outside every scope
 * (on <body> after a menu or modal closed, or on the phone header's palette button); null while focus is in a scope
 * or a layer, or when no such page is open. The palette's origin (W2 step 0) and the Cmd/Ctrl+K context chip on a
 * project page (W3b step 0, projects-labels-settings 8; registry projects.context-menu AC7) read it.
 */
export function openPageScopes(kinds: readonly ('issue' | 'project')[] = ['issue', 'project'], doc: Document | undefined = globalThis.document): ActiveScopes | null {
  const o = activeScopes(undefined, doc);
  if (o.layer || o.target !== null || o.scopes.some((s) => s !== 'global')) return null;
  for (const k of kinds) {
    const page = doc?.querySelector(`[data-scope="${k}"]`);
    if (page) return activeScopes(page, doc);
  }
  return null;
}

const EDITABLE = 'input, textarea, select, [contenteditable=""], [contenteditable="true"], [contenteditable="plaintext-only"], [role="combobox"], [role="textbox"]';

/** Focus is in a text field: only ⌘/Ctrl combos, Escape and `allowInEditable` commands are considered. */
export function isEditableTarget(el: EventTarget | null): boolean {
  if (!el || !(el as Element).closest) return false;
  const e = el as Element;
  if (e instanceof HTMLInputElement) {
    const t = e.type;
    return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file', 'image'].includes(t);
  }
  return !!e.closest(EDITABLE);
}

/** The document has a non-collapsed text selection (the editor's mod+k link rule). */
export function hasTextSelection(): boolean {
  try {
    const s = globalThis.getSelection?.();
    return !!s && !s.isCollapsed && s.toString().length > 0;
  } catch {
    return false;
  }
}
